import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { NavBar } from "@/components/NavBar";
import { api } from "@/lib/api";
import { T } from "@/lib/theme";

export default function ClaimStation() {
  const [searchParams] = useSearchParams();
  const preselectId = searchParams.get("station");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelected, setBulkSelected] = useState([]); // station IDs
  const [bulkId, setBulkId] = useState("");
  const [ncaNum, setNcaNum] = useState("");
  const [ncaCheck, setNcaCheck] = useState(null); // {found: bool, entry?}
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [step, setStep] = useState(1);
  const [code, setCode] = useState("");
  const [verifyCodeHint, setVerifyCodeHint] = useState("");
  const [delivery, setDelivery] = useState(null);
  const [claimId, setClaimId] = useState("");
  const [expiresAt, setExpiresAt] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [resentMsg, setResentMsg] = useState("");
  const [licenceFile, setLicenceFile] = useState(null);
  const [licenceDocId, setLicenceDocId] = useState("");
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // 1-second ticker driving the countdown on the verify screen
  useEffect(() => {
    if (step !== 3 || !expiresAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [step, expiresAt]);

  const remaining = expiresAt
    ? Math.max(0, Math.floor((new Date(expiresAt).getTime() - now) / 1000))
    : null;
  const remainingLabel = remaining !== null
    ? `${String(Math.floor(remaining / 60)).padStart(2, "0")}:${String(remaining % 60).padStart(2, "0")}`
    : null;

  const resendCode = async () => {
    setLoading(true); setError(""); setResentMsg("");
    try {
      const { data } = await api.post("/auth/resend-claim-code", { claim_id: claimId });
      setExpiresAt(data.expires_at);
      setVerifyCodeHint(data.verify_code || "");
      setDelivery(data.delivery);
      setCode("");
      setResentMsg("A fresh code has been sent.");
    } catch (e) {
      setError(e.response?.data?.detail || "Could not resend code");
    } finally { setLoading(false); }
  };

  const search = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/stations?name=${encodeURIComponent(query)}&limit=20`);
      setResults(data.stations || []);
    } catch (e) {
      setError("Search failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Deep-link support: when the user arrives via /claim?station=ID
  // (e.g. from the "Own this station?" CTA on Listen), pre-select and
  // jump straight to step 2 so they skip the search-by-name screen.
  useEffect(() => {
    if (!preselectId || selected) return;
    api.get(`/stations/${preselectId}`).then((r) => {
      if (r.data) {
        setSelected(r.data);
        setStep(2);
      }
    }).catch(() => {});
  }, [preselectId, selected]);

  const uploadLicence = async (file) => {
    if (!file) return;
    setUploadingDoc(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/auth/claim-station/upload-licence", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLicenceFile(file);
      setLicenceDocId(data.doc_id);
      toast.success("Licence uploaded", { description: `${data.filename} · ${(data.size / 1024).toFixed(0)} KB` });
    } catch (e) {
      const detail = e.response?.data?.detail || "Upload failed";
      setError(typeof detail === "string" ? detail : "Upload failed — file may be too large (>5 MB) or wrong type (PDF/JPG/PNG only).");
    } finally {
      setUploadingDoc(false);
    }
  };

  const submitClaim = async () => {
    if (!selected) return;
    if (!ncaNum) { setError("NCA licence number is required."); return; }
    if (!licenceDocId) { setError("Upload a copy of your NCA licence (PDF/JPG/PNG)."); return; }
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/claim-station", {
        station_id: selected.id,
        nca_licence_number: ncaNum,
        licence_doc_id: licenceDocId,
        contact_phone: contactPhone || undefined,
      });
      setClaimId(data.claim_id);
      setExpiresAt(data.expires_at);
      setVerifyCodeHint(data.verify_code || "");
      setDelivery(data.delivery);
      setStep(3);
      toast.success("Code dispatched", {
        description: `We sent a 6-digit code to the NCA-registered email ${data.code_dispatched_to}. Check the broadcaster's official inbox.`,
        duration: 8000,
      });
    } catch (e) {
      // Backend returns structured {code, message} for 422s — surface the message
      const detail = e.response?.data?.detail;
      let msg = "Claim failed";
      if (typeof detail === "string") msg = detail;
      else if (detail && detail.message) msg = detail.message;
      else if (Array.isArray(detail)) msg = detail[0]?.msg || msg;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const verifyClaim = async () => {
    setLoading(true);
    setError("");
    try {
      if (bulkMode) {
        await api.post("/auth/verify-claim-bulk", { bulk_id: bulkId, code });
      } else {
        await api.post("/auth/verify-claim", { claim_id: claimId, code });
      }
      setStep(4);
      toast.success("Code accepted — in admin review", {
        description: "Approval lands in ≤48 hours via the NCA-registered email.",
        duration: 6000,
      });
    } catch (e) {
      const detail = e.response?.data?.detail || "Verification failed";
      setError(detail);
      toast.error("Verification failed", { description: detail });
    } finally {
      setLoading(false);
    }
  };

  const checkNca = async (val) => {
    setNcaNum(val);
    setNcaCheck(null);
    if (!val || val.length < 6) return;
    try {
      const { data } = await api.get(`/nca/lookup?licence_number=${encodeURIComponent(val)}`);
      setNcaCheck(data);
    } catch { setNcaCheck(null); }
  };

  const toggleBulkSelect = (s) => {
    setBulkSelected((prev) =>
      prev.find((x) => x.id === s.id) ? prev.filter((x) => x.id !== s.id) : [...prev, s]);
  };

  const submitBulkClaim = async () => {
    if (bulkSelected.length === 0) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/claim-stations-bulk", {
        station_ids: bulkSelected.map((s) => s.id),
        nca_licence_number: ncaNum,
        contact_email: contactEmail || undefined,
        contact_phone: contactPhone || undefined,
      });
      setBulkId(data.bulk_id);
      setExpiresAt(data.expires_at);
      setVerifyCodeHint(data.verify_code || "");
      setDelivery(data.delivery);
      setStep(3);
    } catch (e) {
      setError(e.response?.data?.detail || "Bulk claim failed");
    } finally { setLoading(false); }
  };

  const inp = { width: "100%", padding: "11px 14px", borderRadius: 8,
    border: `1px solid ${T.border}`, fontSize: 14, outline: "none", fontFamily: "inherit", marginBottom: 12, color: T.navy };

  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: "'Inter',system-ui,sans-serif" }} data-testid="claim-page">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap')`}</style>
      <NavBar dark={false} />
      <div style={{ maxWidth: 600, margin: "60px auto", padding: "0 24px" }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, color: T.navy, marginBottom: 8 }}>Claim Your Station</h1>
        <p style={{ color: T.muted, marginBottom: 32 }}>Verify ownership to unlock StationDash Pro with live ratings for your station.</p>

        <div style={{ display: "flex", gap: 8, marginBottom: 32 }}>
          {["Find station", "Confirm", "Verify", "Done"].map((label, i) => (
            <div key={i} style={{ flex: 1, textAlign: "center" }}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", margin: "0 auto 6px",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700,
                background: step >= i + 1 ? T.green : T.border, color: step >= i + 1 ? "#fff" : T.muted }}>{i + 1}</div>
              <div style={{ fontSize: 11, color: step === i + 1 ? T.navy : T.muted, fontWeight: step === i + 1 ? 600 : 400 }}>{label}</div>
            </div>
          ))}
        </div>

        {error && <div data-testid="claim-error" style={{ color: "#EF4444", fontSize: 13, marginBottom: 16, padding: "10px 14px", background: "#FEF2F2", borderRadius: 8 }}>⚠ {error}</div>}

        {step === 1 && (
          <>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
              <label data-testid="bulk-toggle" style={{ display: "flex", gap: 8, alignItems: "center", color: T.muted, fontSize: 12, cursor: "pointer" }}>
                <input type="checkbox" checked={bulkMode} onChange={(e) => { setBulkMode(e.target.checked); setBulkSelected([]); }}
                  style={{ accentColor: T.green }} />
                Media group? Claim multiple stations in one verification.
              </label>
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              <input data-testid="claim-search-input" style={{ ...inp, flex: 1, marginBottom: 0 }} placeholder="Search your station name..."
                value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} />
              <button data-testid="claim-search-btn" onClick={search} disabled={loading}
                style={{ padding: "11px 20px", background: T.green, color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", fontSize: 14 }}>
                {loading ? "..." : "Search"}
              </button>
            </div>
            {results.map((s) => {
              const isInBulk = bulkSelected.find((x) => x.id === s.id);
              return (
                <div key={s.id} data-testid={`station-result-${s.id}`}
                  onClick={() => { bulkMode ? toggleBulkSelect(s) : (setSelected(s), setStep(2)); }}
                  style={{ padding: "14px 16px", border: `1px solid ${isInBulk ? T.green : T.border}`,
                    borderRadius: 10, marginBottom: 8, cursor: "pointer",
                    background: isInBulk ? "#F0FDF4" : "#fff" }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = T.green}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = isInBulk ? T.green : T.border}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontWeight: 700, color: T.navy }}>{s.name}</div>
                      <div style={{ fontSize: 12, color: T.muted }}>
                        {s.frequency} · {s.city || ""} {s.country} · {s.is_streaming ? "📡 Online stream" : "📻 FM only"}
                        {s.is_verified && <span style={{ marginLeft: 8, color: T.green, fontWeight: 600 }}>✓ Already verified</span>}
                      </div>
                    </div>
                    {bulkMode && (
                      <div data-testid={`bulk-check-${s.id}`} style={{ fontSize: 18, color: isInBulk ? T.green : T.border }}>
                        {isInBulk ? "✓" : "○"}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {bulkMode && bulkSelected.length > 0 && (
              <div data-testid="bulk-cart" style={{ position: "sticky", bottom: 20, marginTop: 16,
                padding: "14px 18px", background: T.navy, color: "#fff", borderRadius: 10,
                display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{bulkSelected.length} stations selected</div>
                  <div style={{ fontSize: 11, opacity: 0.7 }}>{bulkSelected.slice(0, 3).map((s) => s.name).join(", ")}{bulkSelected.length > 3 ? ` +${bulkSelected.length - 3}` : ""}</div>
                </div>
                <button data-testid="bulk-continue" onClick={() => setStep(2)}
                  style={{ background: T.greenAccent, color: "#fff", border: "none", padding: "10px 18px", borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 13 }}>
                  Continue →
                </button>
              </div>
            )}
            {results.length === 0 && query && !loading && (
              <div style={{ color: T.muted, fontSize: 14, textAlign: "center", padding: "20px 0" }}>
                No stations found. Try a different name or partial name.
              </div>
            )}
          </>
        )}

        {step === 2 && (bulkMode ? bulkSelected.length > 0 : selected) && (
          <>
            <div style={{ padding: "16px", background: "#F0FDF4", borderRadius: 10, marginBottom: 20, border: `1px solid #BBF7D0` }}>
              {bulkMode ? (
                <>
                  <div style={{ fontWeight: 700, color: T.navy }}>{bulkSelected.length} stations</div>
                  <div style={{ fontSize: 12, color: T.muted }}>{bulkSelected.map((s) => s.name).join(", ")}</div>
                </>
              ) : (
                <>
                  <div style={{ fontWeight: 700, color: T.navy }}>{selected.name}</div>
                  <div style={{ fontSize: 12, color: T.muted }}>{selected.frequency} · {selected.country}</div>
                </>
              )}
            </div>
            <div data-testid="claim-security-banner" style={{ padding: "12px 14px", background: "#FEF3C7",
                border: "1px solid #FCD34D", borderRadius: 8, marginBottom: 18, fontSize: 12, color: "#92400E" }}>
              <b>🛡️ Why these gates?</b> To stop social-engineering fraud, your 6-digit code goes to the
              broadcaster's <b>NCA-registered email</b>, NOT one you type. Approval is final after a manual
              admin review (≤48 hours).
            </div>
            <label style={{ fontSize: 12, fontWeight: 700, color: T.muted, display: "block", marginBottom: 6, letterSpacing: "0.06em" }}>
              NCA BROADCAST LICENCE NUMBER <span style={{ color: "#EF4444" }}>(required)</span>
            </label>
            <input data-testid="claim-nca-input" style={inp} placeholder="e.g. FM/ACC/2019/0042"
              value={ncaNum} onChange={(e) => checkNca(e.target.value)} />
            {ncaCheck && ncaCheck.found && (
              <div data-testid="nca-found" style={{ background: "#F0FDF4", color: "#166534", border: "1px solid #BBF7D0",
                borderRadius: 8, padding: "8px 12px", fontSize: 12, marginTop: -8, marginBottom: 14 }}>
                ✓ Found in NCA registry: <b>{ncaCheck.entry.station_name}</b> · {ncaCheck.entry.licensee} · expires {ncaCheck.entry.expires}
              </div>
            )}
            {ncaCheck && !ncaCheck.found && (
              <div data-testid="nca-notfound" style={{ background: "#FEE2E2", color: "#991B1B", border: "1px solid #FECACA",
                borderRadius: 8, padding: "8px 12px", fontSize: 12, marginTop: -8, marginBottom: 14 }}>
                ✗ Not in our NCA registry mirror. Email <a href="mailto:hello@wavescorepro.com" style={{ color: "#991B1B", fontWeight: 700 }}>hello@wavescorepro.com</a> to add your authorisation, then return to claim.
              </div>
            )}

            <label style={{ fontSize: 12, fontWeight: 700, color: T.muted, display: "block", marginBottom: 6, letterSpacing: "0.06em" }}>
              NCA LICENCE DOCUMENT <span style={{ color: "#EF4444" }}>(required · PDF / JPG / PNG · ≤5 MB)</span>
            </label>
            <div data-testid="claim-licence-dropzone" style={{
              border: `2px dashed ${licenceDocId ? T.green : T.border}`,
              borderRadius: 10, padding: "20px 16px", textAlign: "center",
              background: licenceDocId ? "#F0FDF4" : "#FAFAFA",
              cursor: uploadingDoc ? "wait" : "pointer", marginBottom: 14,
              transition: "all 0.15s",
            }}
            onClick={() => document.getElementById("licence-file-input")?.click()}>
              <input id="licence-file-input" data-testid="claim-licence-file"
                type="file" accept="application/pdf,image/jpeg,image/png,image/webp"
                onChange={(e) => uploadLicence(e.target.files?.[0])}
                style={{ display: "none" }} />
              {uploadingDoc ? (
                <div style={{ color: T.muted, fontSize: 13 }}>⏳ Uploading…</div>
              ) : licenceDocId ? (
                <div>
                  <div style={{ fontSize: 24, marginBottom: 4 }}>✅</div>
                  <div style={{ color: T.navy, fontWeight: 700, fontSize: 13 }}>{licenceFile?.name}</div>
                  <div style={{ color: T.muted, fontSize: 11, marginTop: 4 }}>Click to replace</div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 28, marginBottom: 4 }}>📎</div>
                  <div style={{ color: T.navy, fontWeight: 600, fontSize: 13 }}>Click or drop your NCA licence here</div>
                  <div style={{ color: T.muted, fontSize: 11, marginTop: 4 }}>Stored encrypted in our review queue. Admin-eyes only.</div>
                </div>
              )}
            </div>

            <label style={{ fontSize: 12, fontWeight: 700, color: T.muted, display: "block", marginBottom: 6, letterSpacing: "0.06em" }}>
              CONTACT PHONE (optional, never used to send the verification code)
            </label>
            <input data-testid="claim-contact-phone" style={inp} placeholder="e.g. +233244111222"
              value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
            <div style={{ fontSize: 11, color: T.muted, marginBottom: 20 }}>
              The 6-digit code is dispatched only to the broadcaster's NCA-registered email — we never send it to anything you type here.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button data-testid="claim-back-btn" onClick={() => setStep(1)}
                style={{ padding: "12px 20px", background: "#fff", border: `1px solid ${T.border}`, borderRadius: 8, cursor: "pointer", fontSize: 14, color: T.muted }}>Back</button>
              <button data-testid="claim-submit-btn" onClick={() => bulkMode ? submitBulkClaim() : submitClaim()} disabled={loading}
                style={{ flex: 1, padding: "12px", background: T.green, color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 15 }}>
                {loading ? "Submitting..." : bulkMode ? `Submit bulk claim (${bulkSelected.length})  →` : "Submit claim →"}
              </button>
            </div>
          </>
        )}

        {step === 3 && (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📧</div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: T.navy, marginBottom: 8 }}>Verify ownership</h2>
            <p style={{ color: T.muted, marginBottom: 18, lineHeight: 1.6 }}>
              Enter the 6-digit verification code to confirm your ownership of {bulkMode ? `${bulkSelected.length} stations` : selected?.name}.
            </p>
            {delivery?.email?.sent && (
              <div data-testid="claim-delivery-email" style={{ background: "#F0FDF4", color: "#166534", border: "1px solid #BBF7D0", borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 12 }}>
                ✓ Code emailed to NCA-registered address <b>{delivery.email.masked_to || delivery.email.to}</b>
              </div>
            )}
            {delivery?.sms?.sent && (
              <div data-testid="claim-delivery-sms" style={{ background: "#F0FDF4", color: "#166534", border: "1px solid #BBF7D0", borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 12 }}>
                ✓ Code sent via SMS to <b>{delivery.sms.masked_to || delivery.sms.to}</b>
              </div>
            )}
            {verifyCodeHint && (
              <div data-testid="claim-code-hint" style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #FCD34D", borderRadius: 8, padding: "10px 14px", fontSize: 12, marginBottom: 16, textAlign: "left" }}>
                <b>Email not configured.</b> Verification code: <code style={{ fontFamily: "monospace", fontWeight: 700 }}>{verifyCodeHint}</code>.
                Once a RESEND_API_KEY is set, this code will be emailed instead.
              </div>
            )}
            <input data-testid="claim-code-input" style={{ ...inp, textAlign: "center", fontSize: 32, fontWeight: 800, letterSpacing: "0.3em", width: 220, margin: "0 auto 8px", display: "block" }}
              placeholder="000000" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />

            {remainingLabel !== null && (
              <div data-testid="claim-countdown" style={{ textAlign: "center", marginBottom: 14, color: remaining < 60 ? "#EF4444" : T.muted, fontSize: 12, fontFamily: "monospace" }}>
                {remaining > 0 ? `Code expires in ${remainingLabel}` : "Code expired"}
              </div>
            )}
            {resentMsg && <div data-testid="claim-resent" style={{ background: "#F0FDF4", color: "#166534", border: "1px solid #BBF7D0", borderRadius: 8, padding: "8px 14px", fontSize: 12, marginBottom: 12 }}>✓ {resentMsg}</div>}

            <button data-testid="claim-verify-btn" onClick={verifyClaim} disabled={loading || code.length !== 6 || remaining === 0}
              style={{ display: "block", width: "100%", padding: "13px", background: T.green, color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, cursor: (code.length === 6 && remaining !== 0) ? "pointer" : "not-allowed", fontSize: 15, opacity: (code.length === 6 && remaining !== 0) ? 1 : 0.6 }}>
              {loading ? "Verifying..." : "Verify and claim station"}
            </button>

            <button data-testid="claim-resend-btn" onClick={resendCode} disabled={loading}
              style={{ display: "block", width: "100%", padding: "10px", background: "transparent", color: T.muted, border: "none", borderRadius: 8, fontSize: 12, cursor: "pointer", marginTop: 12, textDecoration: "underline" }}>
              {remaining === 0 ? "Send me a new code" : "Didn't get the code? Send it again"}
            </button>
          </div>
        )}

        {step === 4 && (
          <div data-testid="claim-success" style={{ textAlign: "center", padding: "40px 0" }}>
            <div style={{ fontSize: 72, marginBottom: 16 }}>⏳</div>
            <h2 style={{ fontSize: 26, fontWeight: 800, color: T.navy, marginBottom: 12 }}>In admin review</h2>
            <p style={{ color: T.muted, marginBottom: 18, lineHeight: 1.6 }}>
              Code accepted ✓ — your claim for <strong>{bulkMode ? `${bulkSelected.length} stations` : selected?.name}</strong> is now in the
              48-hour admin review queue. We will email the outcome to the broadcaster's NCA-registered address.
            </p>
            <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10,
                          padding: "14px 18px", textAlign: "left", marginBottom: 24, fontSize: 13, color: "#166534" }}>
              <b>What happens next?</b>
              <ol style={{ margin: "8px 0 0 18px", padding: 0, lineHeight: 1.7 }}>
                <li>An admin reviews your uploaded licence + cross-checks against the NCA registry.</li>
                <li>You receive an email at the broadcaster's NCA-registered address with the decision.</li>
                <li>If approved, StationDash Pro unlocks immediately. If anything looks off, we'll request a clarification.</li>
              </ol>
            </div>
            <Link to="/dashboard" data-testid="claim-dashboard-link"
              style={{ background: T.green, color: "#fff", padding: "14px 32px", borderRadius: 10, fontWeight: 700, fontSize: 16, textDecoration: "none", display: "inline-block" }}>
              Back to dashboard
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
